import ReactGA from 'react-ga4';

// Initialize Google Analytics
export const initGA = (measurementId?: string): void => {
  // Get measurement ID from environment variable or parameter
  const gaId = measurementId || process.env.REACT_APP_GA_MEASUREMENT_ID;
  
  if (gaId) {
    ReactGA.initialize(gaId, {
      gaOptions: {
        // Enable anonymize IP for privacy compliance
        anonymize_ip: true,
        // Enable site speed sample rate
        siteSpeedSampleRate: 100,
      },
      gtagOptions: {
        // Enable debug mode in development
        debug_mode: process.env.NODE_ENV === 'development',
        // Configure cookie settings for privacy
        cookie_flags: 'SameSite=None;Secure',
      },
    });
    console.log('Google Analytics initialized');
  } else {
    console.warn('Google Analytics measurement ID not provided');
  }
};

// Track page views
export const trackPageView = (path: string, title?: string): void => {
  ReactGA.send({ 
    hitType: 'pageview', 
    page: path,
    title: title || document.title,
  });
};

// Track custom events
export interface AnalyticsEvent {
  category: string;
  action: string;
  label?: string;
  value?: number;
  nonInteraction?: boolean;
}

export const trackEvent = ({ 
  category, 
  action, 
  label, 
  value,
  nonInteraction = false 
}: AnalyticsEvent): void => {
  ReactGA.event({
    category,
    action,
    label,
    value,
    nonInteraction,
  });
};

// Pre-defined event tracking functions for common actions
export const trackThanksBeerGenerated = (params: {
  hasSenderName: boolean;
  hasRecipientName: boolean;
  hasMessage: boolean;
  company?: string;
}): void => {
  trackEvent({
    category: 'ThanksBeer',
    action: 'generated',
    label: params.company || 'no_company',
    value: (params.hasSenderName ? 1 : 0) + (params.hasRecipientName ? 1 : 0) + (params.hasMessage ? 1 : 0),
  });
  
  // Track custom dimensions
  ReactGA.gtag('event', 'thanksbeer_generated', {
    has_sender: params.hasSenderName,
    has_recipient: params.hasRecipientName,
    has_message: params.hasMessage,
    company_context: params.company || 'none',
  });
};

export const trackLinkCopied = (url: string): void => {
  trackEvent({
    category: 'Sharing',
    action: 'link_copied',
    label: url,
  });
  
  ReactGA.gtag('event', 'share', {
    method: 'copy_link',
    content_type: 'thanksbeer_url',
    item_id: url,
  });
};

export const trackBeerDrink = (params: {
  globalCount: number | null;
  localStreak: number;
  hasSender: boolean;
  hasRecipient: boolean;
  hasMessage: boolean;
  company?: string;
}): void => {
  trackEvent({
    category: 'Engagement',
    action: 'beer_drunk',
    label: params.company || 'no_company',
    value: params.localStreak,
  });
  
  ReactGA.gtag('event', 'beer_drunk', {
    global_count: params.globalCount,
    local_streak: params.localStreak,
    has_sender: params.hasSender,
    has_recipient: params.hasRecipient,
    has_message: params.hasMessage,
    company_context: params.company || 'none',
  });
};

export const trackNavigationClick = (destination: string, source: string): void => {
  trackEvent({
    category: 'Navigation',
    action: 'click',
    label: `${source} -> ${destination}`,
  });
};

export const trackButtonClick = (buttonName: string, location: string): void => {
  trackEvent({
    category: 'Button',
    action: 'click',
    label: `${buttonName} (${location})`,
  });
};

export const trackFormInteraction = (formName: string, fieldName: string, action: 'focus' | 'blur' | 'change'): void => {
  trackEvent({
    category: 'Form',
    action: `${formName}_${action}`,
    label: fieldName,
    nonInteraction: action === 'focus', // Don't count focus as interaction
  });
};

export const trackVideoEvent = (action: 'start' | 'complete' | 'pause', videoName: string): void => {
  trackEvent({
    category: 'Video',
    action,
    label: videoName,
  });
  
  ReactGA.gtag('event', `video_${action}`, {
    video_name: videoName,
  });
};

export const trackPDFDownload = (fileName: string): void => {
  trackEvent({
    category: 'Download',
    action: 'pdf_download',
    label: fileName,
  });
  
  ReactGA.gtag('event', 'file_download', {
    file_name: fileName,
    file_extension: 'pdf',
  });
};

export const trackError = (errorMessage: string, errorLocation: string, fatal: boolean = false): void => {
  trackEvent({
    category: 'Error',
    action: errorLocation,
    label: errorMessage,
  });
  
  ReactGA.gtag('event', 'exception', {
    description: errorMessage,
    fatal,
  });
};

export const trackTiming = (category: string, variable: string, value: number, label?: string): void => {
  ReactGA.gtag('event', 'timing_complete', {
    name: variable,
    value,
    event_category: category,
    event_label: label,
  });
};

// Track user properties
export const setUserProperty = (propertyName: string, value: string): void => {
  ReactGA.gtag('set', 'user_properties', {
    [propertyName]: value,
  });
};

// Track user ID for cross-device tracking (only if user is authenticated)
export const setUserId = (userId: string): void => {
  const gaId = process.env.REACT_APP_GA_MEASUREMENT_ID;
  if (gaId) {
    ReactGA.gtag('config', gaId, {
      user_id: userId,
    });
  } else {
    console.warn('Cannot set user ID: Google Analytics measurement ID not configured');
  }
};

// Enhanced E-commerce tracking (for future monetization)
export const trackPurchase = (params: {
  transactionId: string;
  value: number;
  currency?: string;
  items: Array<{
    id: string;
    name: string;
    category?: string;
    quantity: number;
    price: number;
  }>;
}): void => {
  ReactGA.gtag('event', 'purchase', {
    transaction_id: params.transactionId,
    value: params.value,
    currency: params.currency || 'USD',
    items: params.items,
  });
};

// Export all functions as a named object
const analytics = {
  initGA,
  trackPageView,
  trackEvent,
  trackThanksBeerGenerated,
  trackLinkCopied,
  trackBeerDrink,
  trackNavigationClick,
  trackButtonClick,
  trackFormInteraction,
  trackVideoEvent,
  trackPDFDownload,
  trackError,
  trackTiming,
  setUserProperty,
  setUserId,
  trackPurchase,
};

export default analytics;
